/**
 * How an answer shows where its information came from.
 *
 * Two rules, both about not overstating:
 *
 *  - **"Searched the web" only when the provider says a search ran.** The
 *    server sends the provider's own count; this never infers a search from
 *    the answer's wording or from links in it.
 *  - **Sources are the provider's citations and nothing else.** A link the
 *    model wrote into its prose is made clickable where it sits, because it is
 *    part of what it said — but it is not listed as a source, because a URL in
 *    text is a claim and a citation is a page the search actually returned.
 */

import type { ReactNode } from 'react';
import { ExternalLink, Globe } from 'lucide-react';
import type { AiSource } from '@cenox/shared';

/** The host, without `www.`, for a short label. */
function host(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** http(s) only — never `javascript:` or `data:`, whatever the text says. */
function safeHref(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

const LINK = /\[([^\]\n]{1,300})\]\((https?:\/\/[^\s)]{1,2000})\)/g;

/**
 * Answer text with its Markdown links made clickable, and nothing else
 * interpreted. No HTML is ever produced from the text: everything that is not
 * a well-formed http(s) link stays exactly the characters it was.
 */
export function AnswerText({ text }: { text: string }): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const [whole, label, url] = match;
    const href = safeHref(url!);
    if (!href) continue;
    parts.push(text.slice(last, match.index));
    parts.push(
      <a
        key={`${match.index}-${href}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="text-brand-700 underline decoration-brand-300 underline-offset-2 hover:decoration-brand-600"
      >
        {label}
      </a>,
    );
    last = match.index! + whole.length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

/**
 * Below an answer: whether the web was searched, and the sources it cited.
 *
 * `calls` null means search was not on for this answer, and nothing is shown —
 * an answer from the business's records or from the model alone is not
 * labelled as anything it was not.
 */
export function WebSources({ calls, sources }: { calls: number | null | undefined; sources: AiSource[] | null | undefined }): ReactNode {
  if (calls === null || calls === undefined) return null;

  if (calls === 0) {
    return (
      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
        <Globe className="size-3.5" aria-hidden />
        Search the web was on — this answer did not need a search.
      </p>
    );
  }

  const list = sources ?? [];
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" aria-label="Web sources">
      <p className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
        <Globe className="size-3.5 text-brand-600" aria-hidden />
        From a live web search · {calls} {calls === 1 ? 'search' : 'searches'}
        <span className="font-normal text-slate-400">— not from your business records</span>
      </p>
      {list.length ? (
        <ol className="mt-1.5 space-y-1">
          {list.map((s, i) => {
            const href = safeHref(s.url);
            if (!href) return null;
            return (
              <li key={href} className="flex min-w-0 items-baseline gap-1.5 text-xs">
                <span className="shrink-0 text-slate-400">{i + 1}.</span>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex min-w-0 items-center gap-1 text-brand-700 hover:underline"
                >
                  <span className="truncate">{s.title || host(href)}</span>
                  <ExternalLink className="size-3 shrink-0" aria-hidden />
                </a>
                <span className="shrink-0 text-slate-400">{host(href)}</span>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          The search returned no citations for this answer. Treat any current facts in it as unconfirmed.
        </p>
      )}
    </div>
  );
}
